import type { ContextMenuAction } from '../../../explorer/models/TreeModel';
import type { StorageService } from '../../../../types/azureStorage';
import type { StorageMenuNode } from '../../../database-objects/services/storageContextMenuService';
import {
  registerStorageTreeExtension,
  type StorageMenuContext,
  type StorageServiceFolderAction,
} from '../../../database-objects/services/storageTreeExtensions';
import { STORAGE_READ_ONLY_REASON } from '../../../../core/engines/azureStorageRefusal';

export type QueueDialogRequest = {
  kind: 'create-queue' | 'add-message' | 'clear-queue' | 'delete-queue';
  connectionId: string;
  queue?: string;
  source: 'tree';
};
export type QueueMenuContext = {
  openDialog: (request: QueueDialogRequest) => void;
  track?: (eventName: string, data?: Record<string, string | number | boolean>) => void;
};

let registeredOpener: QueueMenuContext['openDialog'] | undefined;
export function registerS5QueueDialogOpener(opener: QueueMenuContext['openDialog']): void {
  registeredOpener = opener;
}

const mutation = (id: string, label: string, readOnly: boolean): ContextMenuAction => ({
  id, label, ...(readOnly ? { disabled: true, disabledReason: STORAGE_READ_ONLY_REASON } : {}),
});

/** S5 menu factory for S2's tree-extension registry (`getStorageMenuItems`). */
export function s5MenuItems(node: StorageMenuNode, ctx: StorageMenuContext): ContextMenuAction[] {
  if (node.type === 'storage-action') return [];
  if (node.type === 'storage-service-folder') {
    return node.metadata.service === 'queue' ? [mutation('create-queue', 'Create queue…', ctx.readOnly)] : [];
  }
  if (node.metadata.resource.kind !== 'queue') return [];
  return [
    mutation('add-queue-message', 'Add message…', ctx.readOnly),
    mutation('clear-queue', 'Clear queue…', ctx.readOnly),
    mutation('delete-queue', 'Delete queue…', ctx.readOnly),
  ];
}

/** The persistent `Create queue…` row under the Queues folder (S2's `serviceFolderActionRows` seam). */
export const s5ServiceFolderActionRows = (service: StorageService): readonly StorageServiceFolderAction[] =>
  service === 'queue' ? [{ id: 'create-queue', label: 'Create queue…' }] : [];

export function registerS5StorageTreeExtension(): () => void {
  return registerStorageTreeExtension({ id: 's5-queues', slice: 's5', menuItems: s5MenuItems, serviceFolderActionRows: s5ServiceFolderActionRows });
}

export function executeS5MenuAction(actionId: string, node: StorageMenuNode, context?: QueueMenuContext): boolean {
  const kind = actionId === 'create-queue' ? 'create-queue' : actionId === 'add-queue-message' ? 'add-message' : actionId === 'clear-queue' ? 'clear-queue' : actionId === 'delete-queue' ? 'delete-queue' : null;
  if (!kind) return false;
  const resource = node.metadata.resource;
  const queue = resource.kind === 'queue' ? resource.queue : undefined;
  const openDialog = context?.openDialog ?? registeredOpener;
  if (!openDialog) return true;
  openDialog({ kind, connectionId: node.metadata.connectionId, ...(queue ? { queue } : {}), source: 'tree' });
  return true;
}
